import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Row, Col } from 'reactstrap';

import CheckboxInputField from 'components/layout/forms/CheckboxInputField';
import NumberInputField from 'components/layout/forms/NumberInputField';
import SelectInputFieldControlled from 'components/layout/forms/SelectInputFieldControlled';
import TextInputField from 'components/layout/forms/TextInputField';
import translations from 'services/translations/translations';
import store from 'store/store';

import {
  is2kAluGlazy,
  isSlidy30,
  isSlidy36,
} from 'components/configurator/types/glazingWallTypesCheck';

import maximize from 'assets/maximize.svg';
import minimize from 'assets/minimize.svg';
import AluGlazyFloorProfile from './fields/AluGlazyFloorProfile';
import AluGlazyRoofProfile from './fields/AluGlazyRoofProfile';
import getFieldErrorMessage from './getFieldErrorMessage';
import glassJointTypes from '../../../types/glassJointTypes';
import updateGlazingWallTypeDependencies from './updateGlazingWallTypeDependencies';
import updateGlazingWallTypeDependenciesOnDoors from './updateGlazingWallTypeDependenciesOnDoors';
import updateDependenciesBasedOnFirstDoorLastDoor from './updateDependenciesBasedOnFirstDoorLastDoor';
import getOptions from '../../../services/getOptions';
import glassListTypes from '../../../types/glassListTypes';
import GlassType from './fields/GlassType';
import glazingWallTypes from '../../../types/glazingWallTypes';
import handleGlassWallProperties from './handleGlassWallProperties';
import IValidationError from './IValidationError';
import { verticalProfileOptions } from '../../../types/doorOptions';

import 'style/configurator/Modal.css';
import PrimaryButton from '../../../../layout/buttons/PrimaryButton';
import getTargetValue from '../../../../../services/forms/getTargetValue';
import TrashIcon from '../../../../layout/buttons/TrashIcon';

const GlassWallProperties = ({
  validationErrors,
  setChangedGlazingType,
  runningGlazingWallDependencies,
  setChangedFirstLastDoorInWall,
  changedFirstLastDoorInWall,
  disableInputs,
}: {
  validationErrors: IValidationError[];
  setChangedGlazingType: (changed: boolean) => void;
  runningGlazingWallDependencies: () => boolean;
  setChangedFirstLastDoorInWall: (changed: boolean) => void;
  changedFirstLastDoorInWall: boolean;
  disableInputs?: boolean;
}): JSX.Element | null => {
  const wall = useSelector((state: any) => state.selectedWall.properties);
  const { t } = useTranslation([translations.glassWallProperties]);
  const editFieldsClassName = 'edit-fields';
  const [changedFloorProfileType, setChangedFloorProfileType] =
    useState<boolean>(false);
  const [leftVerticalProfilesList, setLeftVerticalProfilesList] = useState<
    JSX.Element[]
  >([]);
  const [rightVerticalProfilesList, setRightVerticalProfilesList] = useState<
    JSX.Element[]
  >([]);
  const [moreIsOpen, setMoreIsOpen] = useState(false);
  const [colorOfFloor, setColorOfFloor] = useState('NCS(2502-B)');

  const glazingWallOptions = getOptions(
    glazingWallTypes,
    translations.glassWallProperties,
    'glazingWallTypes'
  );

  const glassJointOptions = getOptions(
    ['-', ...glassJointTypes],
    translations.glassWallProperties,
    'glassJointOptions'
  );

  const glassListOptions = getOptions(
    ['-', ...glassListTypes],
    translations.glassWallProperties,
    ''
  );

  const aluglazy2ksideOptions = getOptions(
    ['inside', 'outside'],
    translations.glassWallProperties,
    'aluglazy2ksideOptions'
  );

  const getUpdatedWall = () => store.getState().selectedWall.properties;

  const leftVerticalProfileTypeOptions = () =>
    setLeftVerticalProfilesList(
      verticalProfileOptions('left', getUpdatedWall())
    );

  const rightVerticalProfileTypeOptions = () =>
    setRightVerticalProfilesList(
      verticalProfileOptions('right', getUpdatedWall())
    );

  const toggleMore = () => setMoreIsOpen(!moreIsOpen);

  useEffect(() => {
    setChangedFloorProfileType(true);

    if (runningGlazingWallDependencies()) {
      updateGlazingWallTypeDependencies(getUpdatedWall());
      updateGlazingWallTypeDependenciesOnDoors(getUpdatedWall());
    }

    leftVerticalProfileTypeOptions();
    rightVerticalProfileTypeOptions();
  }, [wall.glazingWallType]);

  useEffect(() => {
    // In case floor changes to SE0026, right and left change also
    leftVerticalProfileTypeOptions();
    rightVerticalProfileTypeOptions();
  }, [wall.aluGlazyFloorProfileType]);

  useEffect(() => {
    if (runningGlazingWallDependencies() || changedFirstLastDoorInWall) {
      updateDependenciesBasedOnFirstDoorLastDoor(getUpdatedWall());
      setChangedFirstLastDoorInWall(false);
      setChangedFloorProfileType(false);
    }
  }, [
    rightVerticalProfilesList,
    leftVerticalProfilesList,
    changedFirstLastDoorInWall,
  ]);

  const runningFloorProfileDependencies = () => changedFloorProfileType;

  const handleGlazingWallType = (e: any) => {
    setChangedGlazingType(true);
    handleGlassWallProperties({
      target: {
        name: 'glazingWallType',
        value: e.target.value,
      },
    });
  };

  const isWallTypeElements = () => {
    return t(
      `${translations.glassWallProperties}:glazingWallTypes:${wall.glazingWallType}`
    )
      .toLowerCase()
      .includes('elements');
  };

  const setFloorColor = (e: any) => {
    setColorOfFloor(getTargetValue(e));
  };

  const addSpecialFloor = () => {
    window.AddSpecialFloor({
      color: colorOfFloor,
    });
  };

  const getMorePanel = () => {
    if (!moreIsOpen) {
      return null;
    }
    return (
      <>
        <Row>
          <Col>
            <NumberInputField
              className={editFieldsClassName}
              id="insideCeilingHeight"
              label={t(
                `${translations.glassWallProperties}:fields:Inside Height`
              )}
              name="insideCeilingHeight"
              onChange={handleGlassWallProperties}
              value={wall.insideCeilingHeight}
              disabled={disableInputs}
            />
          </Col>
          <Col>
            <NumberInputField
              className={editFieldsClassName}
              id="outsideCeilingHeight"
              label={t(
                `${translations.glassWallProperties}:fields:Outside Height`
              )}
              name="outsideCeilingHeight"
              onChange={handleGlassWallProperties}
              value={wall.outsideCeilingHeight}
              disabled={disableInputs}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <CheckboxInputField
              id="includeCeiling"
              label={t(
                `${translations.glassWallProperties}:fields:Include Ceiling`
              )}
              onChange={handleGlassWallProperties}
              value={wall.includeCeiling}
              name="includeCeiling"
              disabled={disableInputs}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <TextInputField
              className={editFieldsClassName}
              id="buildingWallColor"
              label={t(
                `${translations.glassWallProperties}:fields:Building Wall Color`
              )}
              name="buildingWallColor"
              onChange={handleGlassWallProperties}
              value={wall.buildingWallColor}
              disabled={disableInputs}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <TextInputField
              className={editFieldsClassName}
              id="coloroffloor"
              label={t(
                `${translations.glassWallProperties}:fields:Color of Floor`
              )}
              name="coloroffloor"
              onChange={setFloorColor}
              value={colorOfFloor}
              disabled={disableInputs}
            />
          </Col>
          <Col className="label-top-offset">
            <PrimaryButton
              onClick={addSpecialFloor}
              className="modal-add-button"
            >
              {t(
                `${translations.common}:Wall Actions.Add Special Floor`
              ).toLocaleUpperCase()}
            </PrimaryButton>
            <TrashIcon
              className="modal-delete-button"
              onClick={window.DeleteSpecialFloor}
            />
          </Col>
        </Row>
      </>
    );
  };

  return (
    <div>
      <Row>
        <Col>
          <SelectInputFieldControlled
            id="glazingWallType"
            label={t(
              `${translations.glassWallProperties}:fields:Glazing Wall Type`
            )}
            onChange={handleGlazingWallType}
            name="glazingWallType"
            required
            options={glazingWallOptions}
            value={wall.glazingWallType}
            className={editFieldsClassName}
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <TextInputField
            id="profileColor"
            label={t(
              `${translations.glassWallProperties}:fields:Profile Color`
            )}
            onChange={handleGlassWallProperties}
            value={wall.profileColor}
            name="profileColor"
            required
            className={editFieldsClassName}
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <SelectInputFieldControlled
            id="glassJoint"
            label={t(`${translations.glassWallProperties}:fields:Glass Joint`)}
            onChange={handleGlassWallProperties}
            name="glassJoint"
            required
            options={glassJointOptions}
            value={wall.glassJoint}
            className={editFieldsClassName}
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <TextInputField
            id="listColor"
            label={t(`${translations.glassWallProperties}:fields:List Color`)}
            onChange={handleGlassWallProperties}
            value={wall.listColor}
            name="listColor"
            required
            className={editFieldsClassName}
            disabled={disableInputs}
          />
        </Col>
      </Row>
      <Row>
        <GlassType disableInputs={disableInputs} />
        <Col>
          <SelectInputFieldControlled
            className={editFieldsClassName}
            id="glassList"
            label={t(
              `${translations.glassWallProperties}:fields:Glass Include List`
            )}
            name="glassList"
            onChange={handleGlassWallProperties}
            options={glassListOptions}
            required
            value={wall.glassList}
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <NumberInputField
            className={editFieldsClassName}
            error={getFieldErrorMessage('openingHeight', validationErrors)}
            id="openingHeight"
            label={t(
              `${translations.glassWallProperties}:fields:Opening Height`
            )}
            name="openingHeight"
            onChange={handleGlassWallProperties}
            required
            value={wall.openingHeight}
            disabled={disableInputs}
          />
        </Col>

        <Col className={isSlidy30(wall) || isSlidy36(wall) ? 'hidden' : ''}>
          <NumberInputField
            id="positionOffset"
            label={t(
              `${translations.glassWallProperties}:fields:Position Offset`
            )}
            onChange={handleGlassWallProperties}
            value={wall.positionOffset}
            name="positionOffset"
            required
            className={editFieldsClassName}
            disabled={disableInputs}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NumberInputField
            className={editFieldsClassName}
            id="hProfileLeftOffset"
            label={t(
              `${translations.glassWallProperties}:fields:Roof Profile Length Left`
            )}
            name="hProfileLeftOffset"
            onChange={handleGlassWallProperties}
            required
            value={wall.hProfileLeftOffset}
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <NumberInputField
            className={editFieldsClassName}
            id="roofProfileOffset"
            label={t(
              `${translations.glassWallProperties}:fields:Roof Profile Offset`
            )}
            name="roofProfileOffset"
            onChange={handleGlassWallProperties}
            required
            value={wall.roofProfileOffset}
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <NumberInputField
            className={editFieldsClassName}
            id="hProfileRightOffset"
            label={t(
              `${translations.glassWallProperties}:fields:Roof Profile Length Right`
            )}
            name="hProfileRightOffset"
            onChange={handleGlassWallProperties}
            required
            value={wall.hProfileRightOffset}
            disabled={disableInputs}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NumberInputField
            id="leftWallOffset"
            label={t(
              `${translations.glassWallProperties}:fields:Left Wall Offset`
            )}
            onChange={handleGlassWallProperties}
            value={wall.leftWallOffset}
            name="leftWallOffset"
            required
            className={editFieldsClassName}
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <NumberInputField
            id="floorOffset"
            label={t(`${translations.glassWallProperties}:fields:Floor Offset`)}
            onChange={handleGlassWallProperties}
            value={wall.floorOffset}
            name="floorOffset"
            required
            className={editFieldsClassName}
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <NumberInputField
            id="rightWallOffset"
            label={t(
              `${translations.glassWallProperties}:fields:Right Wall Offset`
            )}
            onChange={handleGlassWallProperties}
            value={wall.rightWallOffset}
            name="rightWallOffset"
            required
            className={editFieldsClassName}
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <SelectInputFieldControlled
            id="aluglazy2kside"
            label={t(
              `${translations.glassWallProperties}:fields:Alu glazy 2k side`
            )}
            name="aluglazy2kside"
            onChange={handleGlassWallProperties}
            options={aluglazy2ksideOptions}
            required
            value={wall.aluglazy2kside}
            disabled={!is2kAluGlazy(wall)}
          />
        </Col>
      </Row>
      <Row>
        <AluGlazyFloorProfile
          disableInputs={disableInputs}
          setChangedFloorProfileType={setChangedFloorProfileType}
        />
        <AluGlazyRoofProfile disableInputs={disableInputs} />
        <Col>
          <SelectInputFieldControlled
            id="leftVerticalProfileType"
            label={t(
              `${translations.glassWallProperties}:fields:Left Vertical Profile`
            )}
            name="leftVerticalProfileType"
            onChange={handleGlassWallProperties}
            options={leftVerticalProfilesList}
            required
            value={wall.leftVerticalProfileType}
            disabled={disableInputs || is2kAluGlazy(wall)}
          />
        </Col>
        <Col>
          <SelectInputFieldControlled
            id="rightVerticalProfileType"
            label={t(
              `${translations.glassWallProperties}:fields:Right Vertical Profile`
            )}
            name="rightVerticalProfileType"
            onChange={handleGlassWallProperties}
            options={rightVerticalProfilesList}
            required
            value={wall.rightVerticalProfileType}
            disabled={disableInputs || is2kAluGlazy(wall)}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Col>
            <CheckboxInputField
              id="addCornerPlate2k"
              label={t(`${translations.glassWallProperties}:fields:Add Corner`)}
              onChange={handleGlassWallProperties}
              value={wall.addCornerPlate2k}
              name="addCornerPlate2k"
              required
              disabled={disableInputs}
            />
          </Col>
        </Col>
        {!isWallTypeElements() ? (
          <Col>
            <CheckboxInputField
              id="hasOpenRoof"
              label={t(
                `${translations.glassWallProperties}:fields:Has Open Roof`
              )}
              onChange={handleGlassWallProperties}
              value={wall.hasOpenRoof}
              name="hasOpenRoof"
              required
              disabled={disableInputs}
            />
          </Col>
        ) : null}
      </Row>
      <Row>
        <Col>
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <div onClick={toggleMore} className="header-text title-spacing">
            {moreIsOpen ? (
              <img
                src={minimize}
                alt="toggle-offer"
                className="collapse-icon"
              />
            ) : (
              <img
                src={maximize}
                alt="toggle-offer"
                className="collapse-icon"
              />
            )}
            {t(`${translations}:More`).toUpperCase()}
          </div>
        </Col>
      </Row>
      {getMorePanel()}
    </div>
  );
};

export default GlassWallProperties;
